import React from 'react';
import { User } from '../../../../../../models/interfaces/User.interface';
import { BusinessObject } from '../../../../../../models/interfaces/BusinessObject.interface';
import businessObjectService from '../../../../../../services/business-object.service';
import UserFormComponent from '../../components/user-form/user-form.component';
import BusinessObjectTableComponent from '../../../business-object-context/components/business-object-table/business-object-table.component';
import { Link, useNavigate, useOutletContext } from 'react-router-dom';
import TopUpFormComponent from '../../components/top-up-form/top-up-form.component';
import { Box, Button, LinearProgress, Tab, Tabs } from '@mui/material';
import { Add } from '@mui/icons-material';
import { makeStyles } from '@mui/styles';
import smsService from '../../../../../../services/sms.service';
import CreditOrderRequestFormComponent from '../../components/credit-order-request-form/credit-order-request-form.component';
import userService from '../../../../../../services/user.service';
import { CommunicationUnitPackage } from '../../../../../../models/interfaces/CommunicationUnitPackage.interface';
import adminService from '../../../../../../services/admin.service';
import { LexofficeContact } from '../../../../../../models/interfaces/LexofficeContact.interface';
import routeService from '../../../../../../routes/route.service';
import Container from '@mui/material/Container';
import logger from '../../../../../../commons/logger';
import { PaymentMethod } from '../../../../../../models/interfaces/PaymentMethod.interface';
import UserPaymentMethodTableComponent from '../../components/user-payment-method-table/user-payment-method-table.component';
import UserPaymentFormComponent from '../../components/user-payment-form/user-payment-form.component';
import UserAppQuickAccessComponent from '../../components/user-app-quick-access/user-app-quick-access.component';
import { ApiKey } from '../../../../../../models/interfaces/ApiKey.interface';
import { API_KEY_NAME_FOR_SUPERADMIN } from '../../../../../../models/consts/Other.const';

const useStyles = makeStyles({
  root: {
    width: '100%',
  },
  createNewBoButton: {
    marginBottom: '20px',
  },
});

function TabPanel(props: any) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Container style={{ width: '100%', maxWidth: '100%' }}>
          <Box p={3}>{children}</Box>
        </Container>
      )}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`,
  };
}

export default function UserDetailComponent() {
  const navigate = useNavigate();
  const { user } = useOutletContext<{ user: User }>();

  const [selectedTab, setSelectedTab] = React.useState<number>(0);
  const [isLoading, setIsLoading] = React.useState<boolean>(false);

  const [businessObjects, setBusinessObjects] = React.useState<BusinessObject[]>([]);
  const [admin, setAdmin] = React.useState<User>(null);
  const [creditBudget, setCreditBudget] = React.useState<number>(null);
  const [creditPackages, setCreditPackages] = React.useState<CommunicationUnitPackage[]>([]);
  const [lexofficeContactsOfUser, setLexofficeContactsOfUser] = React.useState<LexofficeContact[]>([]);
  const [paymentMethodsOfUser, setPaymentMethodsOfUser] = React.useState<PaymentMethod[]>([]);
  const [apiKey, setApiKey] = React.useState<string>('');

  const _getCreditBudgetOfUser = async (userId: number) => {
    const data = await smsService.getCreditBudgetOfUser(userId);
    setCreditBudget(data);
  };

  const _getAllBusinessObjectsOfUser = async (userEmail: string) => {
    const data = await businessObjectService.getAllBusinessObjectsOfUser(userEmail);
    setBusinessObjects(data);
  };

  const _getCurrentAdmin = async () => {
    const data = await userService.getCurrentLoggedInUser();
    setAdmin(data);
  };

  const _getAllCreditPackages = async () => {
    const data = await smsService.getAllCommunicationUnitPackages();
    setCreditPackages(data);
  };

  const _getAllLexofficeContactsOfUser = async (userId: number) => {
    const lexofficeConnections = await adminService.fetchAllLexofficeConnections();

    const setOfLexofficeContactIdOfUser = new Set<string>();
    lexofficeConnections?.forEach(c => {
      if (c.userId === userId) {
        setOfLexofficeContactIdOfUser.add(c.contactId);
      }
    });

    if (setOfLexofficeContactIdOfUser?.size > 0) {
      const lexofficeContacts = await adminService.fetchAllLexofficeContacts();
      setLexofficeContactsOfUser(lexofficeContacts?.filter(c => setOfLexofficeContactIdOfUser.has(c.id)));
    }
  };

  const _getAllPaymentMethodsOfUser = async (userId: number) => {
    const data = await adminService.fetchAllPaymentMethodsOfUser(userId);
    setPaymentMethodsOfUser(data);
  };

  const _initApiKey = async (userId: number) => {
    const apiKeys: ApiKey[] = await adminService.fetchAllApiKeysOfUser(userId);

    const createdApiKeys: ApiKey[] = apiKeys?.filter(apiKey => apiKey.name === API_KEY_NAME_FOR_SUPERADMIN);
    if (createdApiKeys?.length) {
      await Promise.all(createdApiKeys.map(apiKey => adminService.deleteApiKeyOfUser(userId, apiKey.id)));
    }

    const apiKeyToUse = await adminService.createApiKeyForUser(userId, API_KEY_NAME_FOR_SUPERADMIN);
    setApiKey(apiKeyToUse.value);
  };

  const _init = async (user: User) => {
    if (!user?.id) {
      return;
    }

    try {
      setIsLoading(true);

      await Promise.all([
        _getCurrentAdmin(),
        _getAllCreditPackages(),
        _getAllBusinessObjectsOfUser(user?.email),
        _getCreditBudgetOfUser(user?.id),
        _getAllLexofficeContactsOfUser(user?.id),
        _getAllPaymentMethodsOfUser(user?.id),
        _initApiKey(user?.id),
      ]);
    } catch (e) {
      logger.error(e);
    } finally {
      setIsLoading(false);
    }
  };

  React.useEffect(() => {
    _init(user);
  }, [user]);

  const handleCreateBo = () => {
    navigate(routeService.getAndFillBusinessObjectCreatePath(user?.id));
  };

  const handleTopUp = () => {
    _getCreditBudgetOfUser(user?.id);
  };

  const tabChange = (event: any, tabIndex: number) => {
    setSelectedTab(tabIndex);
  };

  const classes = useStyles();

  return (
    <>
      {isLoading ? <LinearProgress /> : <></>}

      <Tabs
        value={selectedTab}
        onChange={tabChange}
        aria-label="User details tabs"
        indicatorColor="primary"
        textColor="primary"
        variant="fullWidth"
      >
        <Tab label={'Personal data'} {...a11yProps(0)} />
        <Tab label={'Business object'} {...a11yProps(1)} />
        <Tab label={'Credit'} {...a11yProps(2)} />
        <Tab label={'Payment method'} {...a11yProps(3)} />
        <Tab label={'Apps quick access'} {...a11yProps(4)} />
      </Tabs>

      <TabPanel value={selectedTab} index={0}>
        <UserFormComponent user={user} />
      </TabPanel>

      <TabPanel value={selectedTab} index={1}>
        <Button
          onClick={handleCreateBo}
          variant="contained"
          color="primary"
          className={classes.createNewBoButton}
          startIcon={<Add />}
        >
          Create new business object
        </Button>

        <BusinessObjectTableComponent userId={user?.id} businessObjects={businessObjects} />
      </TabPanel>

      <TabPanel value={selectedTab} index={2}>
        <CreditOrderRequestFormComponent
          admin={admin}
          user={user}
          creditBudget={creditBudget}
          creditPackages={creditPackages}
          lexofficeContactsOfUser={lexofficeContactsOfUser}
        ></CreditOrderRequestFormComponent>

        <TopUpFormComponent ownerId={user?.id} creditBudget={creditBudget} onTopUp={handleTopUp} />
      </TabPanel>

      <TabPanel value={selectedTab} index={3}>
        <UserPaymentMethodTableComponent paymentMethods={paymentMethodsOfUser}></UserPaymentMethodTableComponent>

        <UserPaymentFormComponent
          paymentMethods={paymentMethodsOfUser}
          userId={user?.id}
          userEmail={user?.email}
        ></UserPaymentFormComponent>
      </TabPanel>

      <TabPanel value={selectedTab} index={4}>
        <UserAppQuickAccessComponent apiKey={apiKey} />
      </TabPanel>

      <div>
        <Link to={`${routeService.getUsersPath()}`}>Back to user table</Link>
      </div>
    </>
  );
}
